import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { REASON_CODES, addDays, diffDays, toDay, buildExtendPayload } from './periodExtension';

const formatDay = (day) => (day ? day.split('-').reverse().join('.') : '—');

// Spec 2026-10-09 §9 — aktif dönemde son tarih uzatma. Yalnız ileri: alanların alt sınırı mevcut
// tarih (ve bitişlerde bugün). Değerlendirme bitişi N gün ilerleyince kalibrasyon başlangıç/bitişi
// mevcut değerlerinden N gün kaydırılarak önerilir (İK yalnız daha ileri alabilir).
export default function PeriodExtendDrawer({ period, open, onClose, onSave }) {
  const { t } = useTranslation();

  const current = useMemo(() => ({
    evalEnd: toDay(period?.evalWindowEnd),
    calibStart: toDay(period?.calibWindowStart),
    calibEnd: toDay(period?.calibWindowEnd),
  }), [period]);

  const schema = useMemo(() => (period ? [
    {
      name: 'current', type: 'notice', grid: 12,
      content: t('performance.period.extend.current', {
        evalEnd: formatDay(current.evalEnd), calibStart: formatDay(current.calibStart), calibEnd: formatDay(current.calibEnd),
      }),
    },
    {
      name: 'newEvalWindowEnd', label: t('performance.period.extend.newEvalWindowEnd'), type: 'date', grid: 12,
      hidden: period.status === 'calibration',
      // Alt sınır MEVCUT tarih: dokunulmamış (geçmişte kalmış) değer doğrulamaya takılmasın; "bugünden
      // önce olamaz" kuralını sunucu uygular (performance.period.extendPastDate).
      minDate: current.evalEnd,
      derives: ['newCalibWindowStart', 'newCalibWindowEnd'],
      deriveFields: (value) => {
        if (!value) return null;
        const shift = diffDays(value, current.evalEnd);
        // Geri alındıysa (kayma ≤ 0) kalibrasyon MEVCUT tarihlerine döner — aksi halde kaydet sessizce
        // yalnız-kalibrasyon uzatması yapardı (final inceleme bulgusu).
        if (shift <= 0) return { newCalibWindowStart: current.calibStart, newCalibWindowEnd: current.calibEnd };
        return { newCalibWindowStart: addDays(current.calibStart, shift), newCalibWindowEnd: addDays(current.calibEnd, shift) };
      },
    },
    {
      name: 'newCalibWindowStart', label: t('performance.period.extend.newCalibWindowStart'), type: 'date', grid: 6,
      minDate: current.calibStart,
    },
    {
      name: 'newCalibWindowEnd', label: t('performance.period.extend.newCalibWindowEnd'), type: 'date', grid: 6,
      minDate: current.calibEnd,
    },
    {
      name: 'reasonCode', label: t('performance.period.extend.reasonCode'), type: 'select', grid: 12, required: true,
      options: REASON_CODES.map((id) => ({ id, name: t(`performance.period.extend.reason.${id}`) })), idKey: 'id', labelKey: 'name',
    },
    {
      name: 'note', label: t('performance.period.extend.note'), type: 'textarea', grid: 12, rows: 3, maxLength: 500,
      description: t('performance.period.extend.noteHint'),
    },
  ] : []), [period, current, t]);

  const initialValues = useMemo(() => (period ? {
    newEvalWindowEnd: current.evalEnd,
    newCalibWindowStart: current.calibStart,
    newCalibWindowEnd: current.calibEnd,
    reasonCode: '',
    note: '',
  } : null), [period, current]);

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={t('performance.period.extend.title')}
      schema={schema}
      initialValues={initialValues}
      onSave={(values) => onSave(buildExtendPayload(period, values))}
    />
  );
}
